import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticateService from '../auth/AuthenticateService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AddAccountPage = () => {
  const { token, logout } = AuthenticateService();
  const navigate = useNavigate();

  const [loadingProducts, setLoadingProducts] = useState(true);
  const [addingAccount, setAddingAccount] = useState(false);
  const [savingsProducts, setSavingsProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [accountName, setAccountName] = useState('');
  const [error, setError] = useState('');

  // Base API URL - adjust if needed
  const API_BASE = 'https://core.agritechbanking.com/api/v1';

  useEffect(() => {
    loadUserData();
    fetchSavingsProducts();
  }, []);

  // Load user name from token/local storage
  const loadUserData = async () => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const user = JSON.parse(userData);
        setAccountName(`${user.first_name || ''} ${user.last_name || ''}`.trim());
      }
    } catch (err) {
      console.error('Error loading user data:', err);
    }
  };

  // Fetch savings products list
  const fetchSavingsProducts = async () => {
    try {
      setLoadingProducts(true);
      setError('');

      const response = await fetch(`${API_BASE}/savings-product`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const data = await response.json();

      // Handle different API response formats
      let products = [];
      if (data.status === true || data.status === 1) {
        if (data.message && data.message.data) {
          products = data.message.data;
        } else if (data.data) {
          products = data.data;
        }
      }

      setSavingsProducts(products);

      if (products.length === 0) {
        setError('No account products available at the moment.');
      }

    } catch (err) {
      console.error('Error fetching savings products:', err);
      setError('Failed to load account products. Please try again.');
      toast.error('Unable to load account products');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Add new account
  const handleAddAccount = async (product) => {
    try {
      setAddingAccount(true);
      setSelectedProduct(product);

      const payload = {
        savings_product_id: product.id.toString(),
        saving_type: "virtual"
      };

      const response = await fetch(`${API_BASE}/add-wallet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      // Check for success
      const success = data && (data.status === true || data.status === 1);

      if (success) {
        // Get success message
        let message = 'Account created successfully!';
        if (data.message) {
          if (typeof data.message === 'string') {
            message = data.message;
          } else if (data.message.message) {
            message = data.message.message;
          }
        }

        toast.success(message);

        // Redirect after successful creation
        setTimeout(() => {
          navigate('/accounts'); // Redirect to accounts list page
        }, 1500);

      } else {
        let errorMsg = 'Failed to create account';
        if (data.message) {
          if (typeof data.message === 'string') {
            errorMsg = data.message;
          } else if (data.message.message) {
            errorMsg = data.message.message;
          }
        }
        toast.error(errorMsg);
      }

    } catch (err) {
      console.error('Error adding account:', err);
      toast.error('Network error. Please check your connection.');
    } finally {
      setAddingAccount(false);
    }
  };

  // Product selection handler
  const handleProductSelect = (product) => {
    if (!addingAccount) {
      handleAddAccount(product);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center">
            <button
              onClick={() => navigate(-1)}
              className="btn btn-outline-secondary me-3"
            >
              <i className="fas fa-arrow-left"></i> Back
            </button>
            <div>
              <h2 className="mb-1">Add New Account</h2>
              {accountName && (
                <p className="text-muted mb-0">Welcome, {accountName}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="row justify-content-center">
        <div className="col-lg-8 col-xl-6">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white py-3">
              <h5 className="mb-0">Select Account Type</h5>
              <p className="text-muted small mb-0">
                Choose a savings product to create a new virtual account
              </p>
            </div>

            <div className="card-body p-0">
              {error && !loadingProducts && (
                <div className="alert alert-danger m-3">
                  {error}
                  <button
                    className="btn btn-sm btn-outline-danger ms-2"
                    onClick={fetchSavingsProducts}
                  >
                    Retry
                  </button>
                </div>
              )}

              {/* Loading State */}
              {loadingProducts ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading account products...</p>
                </div>
              ) : (
                /* Products List */
                <div className="list-group list-group-flush">
                  {savingsProducts.map((product, index) => (
                    <div
                      key={product.id || index}
                      className={`list-group-item list-group-item-action border-0 px-4 py-3 ${
                        selectedProduct?.id === product.id ? 'bg-light' : ''
                      }`}
                      onClick={() => handleProductSelect(product)}
                      style={{
                        cursor: addingAccount ? 'not-allowed' : 'pointer',
                        opacity: addingAccount ? 0.7 : 1
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {product.savings_product || product.name || `Product ${index + 1}`}
                          </h6>
                          {product.code && (
                            <small className="text-muted">Code: {product.code}</small>
                          )}
                          {product.description && (
                            <p className="small text-muted mb-0 mt-1">
                              {product.description}
                            </p>
                          )}
                        </div>

                        {/* Adding indicator */}
                        {addingAccount && selectedProduct?.id === product.id ? (
                          <div className="text-primary">
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Creating...</span>
                            </div>
                            <span className="ms-2 small">Creating...</span>
                          </div>
                        ) : (
                          <i className="fas fa-chevron-right text-muted"></i>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="card-footer bg-white border-top-0 py-3">
              <div className="row">
                <div className="col-6">
                  <button
                    onClick={() => navigate('/account')}
                    className="btn btn-outline-secondary w-100"
                    disabled={addingAccount}
                  >
                    Cancel
                  </button>
                </div>
                <div className="col-6">
                  <button
                    onClick={fetchSavingsProducts}
                    className="btn btn-outline-primary w-100"
                    disabled={loadingProducts || addingAccount}
                  >
                    <i className="fas fa-sync me-2"></i>
                    Refresh List
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Information Card */}
          <div className="card shadow-sm border-0 mt-4">
            <div className="card-body">
              <h6 className="mb-3">
                <i className="fas fa-info-circle text-primary me-2"></i>
                About Virtual Accounts
              </h6>
              <ul className="list-unstyled text-muted small mb-0">
                <li className="mb-2">
                  <i className="fas fa-check-circle text-success me-2"></i>
                  Each virtual account is linked to your main profile
                </li>
                <li className="mb-2">
                  <i className="fas fa-check-circle text-success me-2"></i>
                  Separate account number for transactions
                </li>
                <li className="mb-2">
                  <i className="fas fa-check-circle text-success me-2"></i>
                  Real-time balance updates
                </li>
                <li>
                  <i className="fas fa-check-circle text-success me-2"></i>
                  Secure and FDIC insured
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Container */}
      <div className="toast-container position-fixed top-0 end-0 p-3">
        {/* React Toastify will render here */}
      </div>

      {/* Styles */}
      <style jsx>{`
        .list-group-item:hover {
          background-color: #f8f9fa !important;
        }

        .card {
          border-radius: 12px;
        }

        @media (max-width: 768px) {
          .card-body {
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AddAccountPage;